import { environment } from '../../../environments/environment';

export class DocumentsConstants {
    // The per-file MB cap comes from the `MAX_FILE_SIZE_MB` GitHub var (injected into
    // environment.config.json at deploy time); we coerce because CI variable-substitution can
    // write it as a string, and fall back to 250 MB if it's missing or unparseable.
    private static safeMaxFileSizeMb = (): number => {
        const megabytes = Number(environment.maxFileSizeMb);
        return Number.isFinite(megabytes) && megabytes > 0 ? megabytes : 250;
    };

    // Per-file upload size cap enforced client-side before any prepare/PUT. The API is the
    // authority (rejects with 422); this is a fast-fail.
    public static MAX_FILE_SIZE_BYTES = (): number => DocumentsConstants.safeMaxFileSizeMb() * 1024 * 1024;

    // Human-readable form of the cap, shown proactively in the upload UI and reused in the
    // too-large rejection message so the hint and the error always read identically.
    public static MAX_FILE_SIZE_LABEL = (): string => `${DocumentsConstants.safeMaxFileSizeMb()} MB`;

    // The locked, Admin-Only, root-level folder every org is provisioned with at creation (used as the
    // default upload target for the Communications feature's email attachments, and given a distinct
    // list-view row treatment in the Document Center — DEV-2559). There's no dedicated node type/flag
    // for it; it's identified purely by exact name match — see DocumentUtilities.isEmailAttachmentsFolder.
    public static EMAIL_ATTACHMENTS_FOLDER_NAME = (): string => 'Email Attachments';

    // Max folder-name length. The API cap is 255; we sit 5 below it so the API's auto-increment
    // " (N)" suffix on a duplicate (" (1)", " (12)", …) still fits as the number grows.
    public static FOLDER_NAME_MAX_LENGTH = (): number => 250;

    // Max full file-name length (base + extension). Same 5-char headroom below the API's 255 cap for
    // the auto-increment " (N)" suffix. The rename modal subtracts the locked extension to get the
    // editable base-name cap.
    public static FILE_NAME_MAX_LENGTH = (): number => 250;

    // Fallback MIME type when the browser reports an empty File.type (sent to prepare + the blob PUT).
    public static DEFAULT_CONTENT_TYPE = (): string => 'application/octet-stream';

    // How many files to request per page when listing a folder's files (infinite scroll).
    public static FILES_PAGE_SIZE = (): number => 20;

    // Files shown per folder in the document file picker — loaded as a single page (no infinite scroll
    // there); a folder with more surfaces a "narrow down" hint rather than silently truncating.
    public static FILE_PICKER_PAGE_SIZE = (): number => 100;

    // How often to poll the status of any visible Pending file (GET by id, cache-busted) so a scan
    // that clears server-side flips the row to Available/Quarantined without a manual reload. Polling
    // stops on its own once no Pending file remains on screen.
    public static PENDING_STATUS_POLL_INTERVAL_MS = (): number => 30000;

    // Transient blob-PUT retry policy (network error / 5xx). An expired SAS (403) is terminal.
    public static UPLOAD_MAX_RETRIES = (): number => 3;
    public static UPLOAD_RETRY_BASE_DELAY_MS = (): number => 500;

    // How many files upload directly to blob storage concurrently during a bulk upload. The AC calls
    // for 4-6 in flight; the rest queue behind them and start as slots free up.
    public static UPLOAD_CONCURRENCY = (): number => 4;

    // For very large batches, the single bulk-prepare call is split into chunks of this size so no
    // one prepare payload is unbounded. All chunks are prepared up front (before any upload), so the
    // 30-minute SAS lifetime comfortably covers the whole batch's upload time.
    public static PREPARE_CHUNK_SIZE = (): number => 100;

    // Default cross-container item cap. Folder permission changes that cross the Public boundary
    // trigger per-blob moves; the backend rejects when the folder's recursive descendant count
    // exceeds this cap. The FE pre-check exists so the user gets a fast rejection.
    public static CROSS_CONTAINER_ITEM_CAP = (): number => 50;

    // Folders are eager-loaded in full (not infinite-scrolled): we page through this many at a time
    // and keep fetching until the API reports no more, so the whole folder set renders at once.
    public static FOLDER_PAGE_SIZE = (): number => 100;

    // At or below this much free space (as a percent of the allotted total), the usage bar surfaces a
    // low-storage warning prompting the user to free up space.
    public static LOW_STORAGE_WARNING_THRESHOLD_PERCENT = (): number => 10;

    public static JSON_HEADERS = (): { [header: string]: string } => ({
        'Accept': 'application/json',
        'Content-Type': 'application/json'
    });

    // Modal + dropdown element IDs — shared between the documents page and the modal/toolbar
    // components so that open/close and the element's [id] always target the same DOM node.
    public static PERMISSIONS_MODAL_ID = (): string => 'documentPermissionsModal';
    public static RENAME_MODAL_ID = (): string => 'documentRenameModal';
    public static MOVE_FOLDER_MODAL_ID = (): string => 'documentMoveFolderModal';
    public static BULK_MOVE_FILES_MODAL_ID = (): string => 'documentBulkMoveFilesModal';
    public static DELETE_MODAL_ID = (): string => 'documentDeleteConfirmationModal';
    public static BULK_DELETE_FOLDERS_MODAL_ID = (): string => 'documentBulkDeleteFoldersConfirmationModal';
    public static BULK_DELETE_FILES_MODAL_ID = (): string => 'documentBulkDeleteFilesConfirmationModal';
    public static EMPTY_TRASH_MODAL_ID = (): string => 'documentEmptyTrashConfirmationModal';
    public static LEAVE_UPLOAD_MODAL_ID = (): string => 'documentLeaveUploadModal';
    public static INFO_MODAL_ID = (): string => 'documentInfoModal';
    public static FILTER_DROPDOWN_ID = (): string => 'document-filter-dropdown';
}
